import { Duration, Instant, LocalDate, LocalDateTime, ZoneId } from "@js-joda/core";
import { dateFormatter } from "../../../../shared/utils/date-formatter";
import type {
  PatientId,
  PatientVacationId,
  VisitBroadcastId,
  VisitInstanceId,
} from "../../../../shared/schema/schema";

export type EntityProfileCalendarVisitStatus = "deleted" | "missed" | "task" | "visit";

export type EntityProfileCalendarVisit = {
  authorizationStatuses?: Array<{
    backgroundColor?: string;
    message: string;
    tooltip?: string;
  }> | null;
  billedSeconds?: number;
  certification?: string | null;
  clockinTime?: LocalDateTime | string | null;
  clockoutTime?: LocalDateTime | string | null;
  date: LocalDate | string;
  endTime: LocalDateTime | string;
  caregiverId?: number | null;
  hasGpsIssue?: boolean;
  id: VisitInstanceId;
  isBilled?: boolean;
  isOnPayrollDraft?: boolean;
  isOnWeeklyTemplate?: boolean;
  isPaid?: boolean;
  /**
   * Duties this visit was required to perform, clocked out without, and that
   * nobody has made up on another visit. Empty clears the badge, which is what a
   * coordinator reassigning the duty does.
   */
  missedDuties?: Array<{ code: string; label: string }>;
  missedVisitReason?: string | null;
  payrollDraftSeconds?: number;
  paidSeconds?: number;
  patientAddress?: string | null;
  patientId?: PatientId | null;
  parentSplitVisitInstanceId?: VisitInstanceId | null;
  childSplitVisitInstanceId?: VisitInstanceId | null;
  status: EntityProfileCalendarVisitStatus;
  subtitle?: string;
  title: string;
  startTime: LocalDateTime | string;
  visitBatchId?: number | null;
  visitBatchMaxEndDate?: LocalDate | string | null;
  visitBatchMinStartDate?: LocalDate | string | null;
  visitBatchType?: "PERMANENT" | "SINGLE" | string;
  broadcastKind?: EntityCalendarBroadcastKind | null;
  pendingConfirmationUntil?: Instant | string | null;
  addressTimezone?: string | null;
  visitBroadcast?: {
    requests?: number | null;
    startDateTime?: LocalDateTime | string | null;
    visitBroadcastId?: VisitBroadcastId | null;
  } | null;
};

export type EntityProfileCalendarVacation = {
  date: LocalDate | string;
  id: PatientVacationId;
};

export type EntityProfileCalendarNote = {
  date: LocalDate | string | null;
  id: number;
};

export type EntityProfileCalendarPtoRow = {
  certification?: string | null;
  date: LocalDate | string;
  endTime?: LocalDateTime | string | null;
  id: number;
  label?: string | null;
  patientAddress?: string | null;
  patientId?: PatientId | null;
  patientName?: string | null;
  startTime: LocalDateTime | string;
  statusLabel?: string | null;
};

export type EntityProfileCalendarMarker = {
  actions?: Array<{
    isDisabled?: boolean;
    label: string;
    onClick: () => void;
  }>;
  colorScheme?: string;
  date: LocalDate | string;
  detailLabel?: string;
  endDate?: LocalDate | string;
  iconClassName?: string;
  id: string | number;
  label: string;
  summaryKind?: "pto";
  summarySeconds?: number;
  statusCount?: number | null;
  statusKind?: EntityCalendarBroadcastStatus;
  statusLabel?: string;
  pendingConfirmationUntil?: Instant | string | null;
};

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function toEntityCalendarLocalDate(value: LocalDate | string) {
  return value instanceof LocalDate ? value : LocalDate.parse(String(value));
}

export function toEntityCalendarLocalDateTime(value: LocalDateTime | string) {
  return value instanceof LocalDateTime ? value : LocalDateTime.parse(String(value));
}

export function getEntityCalendarMonthName(monthValue: number) {
  return monthNames[monthValue - 1] ?? "";
}

export function formatEntityCalendarTime(value: LocalDateTime | string | null | undefined) {
  if (value === null || value === undefined) {
    return "";
  }

  return dateFormatter.toTime(toEntityCalendarLocalDateTime(value));
}

export function formatEntityCalendarDate(value: LocalDate | string) {
  return dateFormatter.toDate(toEntityCalendarLocalDate(value));
}

export function formatEntityCalendarDuration(seconds: number | null | undefined) {
  if (seconds === null || seconds === undefined || seconds <= 0) {
    return "0:00h";
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);

  return `${hours}:${String(minutes).padStart(2, "0")}h`;
}

export function getEntityCalendarDurationSeconds(
  startTime: LocalDateTime | string | null | undefined,
  endTime: LocalDateTime | string | null | undefined
) {
  if (startTime === null || startTime === undefined || endTime === null || endTime === undefined) {
    return 0;
  }

  return Math.max(
    0,
    Math.floor(
      Duration.between(
        toEntityCalendarLocalDateTime(startTime),
        toEntityCalendarLocalDateTime(endTime)
      ).toMillis() / 1000
    )
  );
}

export function getEntityCalendarVisitDateKey(visit: EntityProfileCalendarVisit) {
  return toEntityCalendarLocalDate(visit.date).toString();
}

export function getEntityCalendarVacationDateKey(vacation: EntityProfileCalendarVacation) {
  return toEntityCalendarLocalDate(vacation.date).toString();
}

export function getEntityCalendarNoteDateKey(note: EntityProfileCalendarNote) {
  return note.date === null ? null : toEntityCalendarLocalDate(note.date).toString();
}

export function getEntityCalendarMarkerDateKey(marker: EntityProfileCalendarMarker) {
  return toEntityCalendarLocalDate(marker.date).toString();
}

export function getEntityCalendarVisibleMonthDates(monthDate: LocalDate) {
  const firstOfMonth = LocalDate.of(monthDate.year(), monthDate.month(), 1);
  const firstDayOffset = firstOfMonth.dayOfWeek().value() % 7;
  const visibleStart = firstOfMonth.minusDays(firstDayOffset);
  const lastOfMonth = firstOfMonth.plusMonths(1).minusDays(1);
  const lastDayOffset = 6 - (lastOfMonth.dayOfWeek().value() % 7);
  const visibleEnd = lastOfMonth.plusDays(lastDayOffset);
  const dates: LocalDate[] = [];

  for (let date = visibleStart; !date.isAfter(visibleEnd); date = date.plusDays(1)) {
    dates.push(date);
  }

  return {
    dates,
    queryFrom: visibleStart.minusDays(1),
    queryTo: visibleEnd.plusDays(1),
    visibleEnd,
    visibleStart,
  };
}

/**
 * Widest range the dashboard calendar endpoints accept, in days between `from`
 * and `to` (the server's `MAX_CALENDAR_RANGE_DAYS`).
 */
export const ENTITY_CALENDAR_MAX_RANGE_DAYS = 62;

/**
 * Splits a range into consecutive, non-overlapping ranges the calendar endpoints
 * accept. The month grid always fits in one; only long list-view ranges split.
 */
export function splitEntityCalendarRange(from: LocalDate, to: LocalDate) {
  const ranges: Array<{ from: LocalDate; to: LocalDate }> = [];

  for (
    let start = from;
    !start.isAfter(to);
    start = start.plusDays(ENTITY_CALENDAR_MAX_RANGE_DAYS + 1)
  ) {
    const end = start.plusDays(ENTITY_CALENDAR_MAX_RANGE_DAYS);
    ranges.push({ from: start, to: end.isAfter(to) ? to : end });
  }

  return ranges;
}

/**
 * Keeps the first item for each key. Used to merge calendar responses of split
 * ranges, where an item spanning two ranges (a broadcast, a PTO) comes back twice.
 */
export function uniqueEntityCalendarItemsBy<T>(items: T[], getKey: (item: T) => unknown): T[] {
  const seen = new Set<unknown>();

  return items.filter((item) => {
    const key = getKey(item);

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

export function chunkEntityCalendarWeeks(dates: LocalDate[]) {
  const weeks: LocalDate[][] = [];

  for (let index = 0; index < dates.length; index += 7) {
    weeks.push(dates.slice(index, index + 7));
  }

  return weeks;
}

/**
 * Broadcast presentation for calendar visits, ported from the AngularJS calendar so
 * the React profile window shows the same thing for the same visit.
 *
 * AngularJS reference:
 *  - the card headline comes from `getMfCalendarItemTypeByVisitInstance`
 *    (public/admin/scripts/components/patientCalendar.js), which maps a visit to
 *    BROADCASTED_VISIT / BROADCASTED_IN_THE_PAST *before* it looks at the caregiver
 *    or the task flag - so a broadcast outranks "Unstaffed"/"Assigned"/"[Task]",
 *    and only a removed or missed visit outranks the broadcast.
 *  - the status pill comes from `visitInstanceService.getVisitInstanceBroadcastStatus`
 *    (public/admin/scripts/services/visitInstanceService.js), which is stricter: it
 *    only shows for an *unstaffed* visit whose start is not before the broadcast's.
 */
export type EntityCalendarBroadcastStatus =
  | "AWAITING_ASSIGNMENT"
  | "PENDING_CONFIRMATION"
  | "PENDING_REQUESTS"
  | "UNSTAFFED";

export type EntityCalendarBroadcastKind = "broadcasted" | "broadcastedInThePast";

function toEntityCalendarInstant(value: Instant | string | null | undefined) {
  if (value === null || value === undefined) {
    return null;
  }

  return value instanceof Instant ? value : Instant.parse(String(value));
}

/**
 * A visit that is part of a broadcast whose start date has already passed - Angular
 * renders it as a plain "Unstaffed" card with no status pill.
 */
function isBroadcastedInThePast(visit: EntityProfileCalendarVisit) {
  const broadcastStart = visit.visitBroadcast?.startDateTime;

  if (broadcastStart === null || broadcastStart === undefined) {
    return false;
  }

  return toEntityCalendarLocalDateTime(visit.startTime).isBefore(
    toEntityCalendarLocalDateTime(broadcastStart)
  );
}

/**
 * The patient calendar's rule (patientCalendar.js): it splits a broadcast whose start
 * has already passed out into its own "Unstaffed"-looking card. The caregiver calendar
 * deliberately does not - see `getCaregiverCalendarVisitBroadcastKind`.
 */
export function getPatientCalendarVisitBroadcastKind(
  visit: EntityProfileCalendarVisit
): EntityCalendarBroadcastKind | null {
  // Angular checks removedAt/missedVisit first, so those cards keep their own look.
  if (visit.status === "deleted" || visit.status === "missed") {
    return null;
  }

  if (visit.visitBroadcast === null || visit.visitBroadcast === undefined) {
    return null;
  }

  return isBroadcastedInThePast(visit) ? "broadcastedInThePast" : "broadcasted";
}

/**
 * The caregiver calendar's rule (caregiverCalendar.js): any visit carrying a broadcast
 * is simply BROADCASTED_VISIT.
 */
export function getCaregiverCalendarVisitBroadcastKind(
  visit: EntityProfileCalendarVisit
): EntityCalendarBroadcastKind | null {
  if (visit.status === "deleted" || visit.status === "missed") {
    return null;
  }

  return visit.visitBroadcast === null || visit.visitBroadcast === undefined ? null : "broadcasted";
}

export function getEntityCalendarVisitBroadcastStatus(
  visit: EntityProfileCalendarVisit
): EntityCalendarBroadcastStatus | null {
  if (visit.visitBroadcast === null || visit.visitBroadcast === undefined) {
    return null;
  }

  // Once a caregiver is assigned the pill is dropped, even though the headline
  // still says "Broadcasting".
  if (visit.caregiverId !== null && visit.caregiverId !== undefined) {
    return null;
  }

  if (visit.status === "deleted" || visit.status === "missed") {
    return null;
  }

  if (isBroadcastedInThePast(visit)) {
    return null;
  }

  const pendingConfirmationUntil = toEntityCalendarInstant(visit.pendingConfirmationUntil);

  if (pendingConfirmationUntil !== null && Instant.now().isBefore(pendingConfirmationUntil)) {
    return "PENDING_CONFIRMATION";
  }

  if ((visit.visitBroadcast.requests ?? 0) > 0) {
    return "AWAITING_ASSIGNMENT";
  }

  const broadcastStart = visit.visitBroadcast.startDateTime;

  if (broadcastStart !== null && broadcastStart !== undefined) {
    const broadcastStartLocal = toEntityCalendarLocalDateTime(broadcastStart);
    // Angular resolves the broadcast start in the visit address' timezone; without a
    // timezone we fall back to comparing wall-clock times, which is what the rest of
    // the calendar does.
    const hasBroadcastStarted =
      visit.addressTimezone === null || visit.addressTimezone === undefined
        ? broadcastStartLocal.isBefore(LocalDateTime.now())
        : Instant.now().isAfter(
            broadcastStartLocal.atZone(ZoneId.of(visit.addressTimezone)).toInstant()
          );

    if (hasBroadcastStarted) {
      return "UNSTAFFED";
    }
  }

  return "PENDING_REQUESTS";
}

export function getEntityCalendarBroadcastStatusLabel(status: EntityCalendarBroadcastStatus) {
  switch (status) {
    case "AWAITING_ASSIGNMENT":
      return "Awaiting Assignment";
    case "PENDING_CONFIRMATION":
      return "Pending Confirmation";
    case "PENDING_REQUESTS":
      return "Pending Requests";
    case "UNSTAFFED":
      return "Unstaffed";
  }
}

/**
 * `countdown-timer` renders hours:minutes (not minutes:seconds), and clamps at
 * "00:00" - see public/admin/modules/shared/components/countdown-timer.
 */
export function formatEntityCalendarCountdown(secondsLeft: number) {
  if (secondsLeft <= 0) {
    return "00:00";
  }

  const hours = Math.floor(secondsLeft / 3600);
  const minutes = Math.floor((secondsLeft % 3600) / 60);

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}
